import { ITemplate } from '../../interface/ITemplate'
import { ITimecard, TimeCardLine } from '../../interface/ISelectedDate'
import axios from 'axios'
import { parseTemplateDateToIso } from './timeUtils'

const FETCH_TEMPLATE_URL = `${process.env.REACT_APP_BACKEND_URL}/Template`
const SAVE_TEMPLATE_URL = `${process.env.REACT_APP_BACKEND_URL}/SaveTemplate`
const DELETE_TEMPLATE_URL = `${process.env.REACT_APP_BACKEND_URL}/Template/{id}`
const SET_DEFAULT_TEMPLATE_URL = `${process.env.REACT_APP_BACKEND_URL}/Template/{id}/SetDefault`

export async function fetchTemplates(config?: any): Promise<any[]> {
    const response = await axios.get<any[]>(FETCH_TEMPLATE_URL, config)
    return response.data
}

export function buildTemplate(selectedDate?: ITimecard, templateName?: string): ITemplate | null {
    if (!selectedDate) return null
    const lines = selectedDate.lines || []

    const mapLine = (l: TimeCardLine) => ({
        workerpersonnelnumber: l.mserp_workerpersonnelnumber || undefined,
        timeandattendancejobid: l.mserp_timeandattendancejobid || '',
        indirectactivitycategoryid: l.mserp_indirectactivitycategoryid || '',
        projectid: l.mserp_projectid || '',
        startdatetime: l.mserp_startdatetime || undefined,
        stopdatetime: l.mserp_stopdatetime || l.mserp_startdatetime || undefined,
        calculatedtimeseconds: l.mserp_calculatedtimeseconds || 0,
        purchid: l.mserp_purchid || '',
        purchlinenumber: l.mserp_purchlinenumber || 0,
        oncalltimeandattendancejobid: l.mserp_oncalltimeandattendancejobid || '',
        mboncallapplicationid: l.mserp_mboncallapplicationid || '',
    })

    const activeLines = lines.filter((l) => !l.delete)

    const tcLines = activeLines.map(mapLine)

    const header = {
        workerpersonnelnumber: lines[0]?.mserp_workerpersonnelnumber || undefined,
        templatename: templateName || undefined,
        traveltime: selectedDate?.header?.mserp_mbquantitytravel || 0,
        default: false,
    }

    return {
        TimeCardHeader: header as any,
        TimeCardLines: tcLines as any,
    } as ITemplate
}

export async function postTemplate(template: ITemplate, config?: any): Promise<any> {
    const response = await axios.post<any>(SAVE_TEMPLATE_URL, template, config)
    return response.data
}

export async function deleteTemplate(id: string, config?: any): Promise<any> {
    const response = await axios.delete<any>(DELETE_TEMPLATE_URL.replace('{id}', id), config)
    return response.data
}

export function applyTemplateToTimecard(selectedDate?: ITimecard, tpl?: ITemplate | any): ITimecard | null {
    if (!selectedDate || !tpl) return null
    const clone: any = JSON.parse(JSON.stringify(selectedDate))
    clone.lines = []
    const dateIso = selectedDate.header?.mserp_timeprofiledate || new Date().toISOString()
    const baseDate = new Date(dateIso)

    const lines = (tpl as any).timeCardLines || []
    for (const tLine of lines) {
        const jobId = tLine.timeandattendancejobid || ''
        if (!jobId) continue
        const line = {
            mserp_timeprofiledate: selectedDate.header?.mserp_timeprofiledate,
            mserp_timeandattendancejobid: jobId,
            mserp_calculatedtimeseconds: 0,
            delete: false,
            mserp_indirectactivitycategoryid: tLine.indirectactivitycategoryid.toUpperCase(),
        } as TimeCardLine
        if (tLine.projectid) line.mserp_projectid = tLine.projectid

        if (tLine.oncalltimeandattendancejobid) {
            line.mserp_oncalltimeandattendancejobid = tLine.oncalltimeandattendancejobid
        }
        if (tLine.mboncallapplicationid !== undefined) {
            line.mserp_mboncallapplicationid = tLine.mboncallapplicationid
        }

        if (tLine.purchid) line.mserp_purchid = tLine.purchid
        if (tLine.purchlinenumber) line.mserp_purchlinenumber = tLine.purchlinenumber

        const startTimeRaw = tLine.startdatetime || undefined
        const stopTimeRaw = tLine.stopdatetime || undefined
        const startIso = parseTemplateDateToIso(startTimeRaw, baseDate, dateIso)
        const stopIso = parseTemplateDateToIso(stopTimeRaw, baseDate, dateIso)
        if (startIso) {
            line.mserp_startdatetime = startIso
            line.mserp_stopdatetime = startIso
        }
        if (stopIso) {
            line.mserp_startdatetime = line.mserp_startdatetime || stopIso
            line.mserp_stopdatetime = stopIso
        }

        if (typeof tLine.calculatedtimeseconds === 'number') {
            line.mserp_calculatedtimeseconds = tLine.calculatedtimeseconds
        }
        clone.lines.push(line)
    }

    const travelTime = (tpl as any).timeCardHeader?.traveltime || 0
    clone.header = clone.header || {}
    clone.header.mserp_mbquantitytravel = travelTime
    return clone as ITimecard
}

export async function setDefaultTemplate(templateId: string, config?: any): Promise<void> {
    if (!templateId) return
    const response = await axios.put<any>(SET_DEFAULT_TEMPLATE_URL.replace('{id}', templateId), {}, config)
    return response.data
}
